import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import PublicPageLayout from "./PublicPageLayout";
import { getPublicSiteSx } from "./publicSiteStyles";
import {
  BILLING_CYCLES,
  PLAN_FEATURES,
  TIERS,
  TIER_DISPLAY,
  TIER_PRICES,
} from "../../constants/membership";

const planOrder = [TIERS.FREE, TIERS.PREMIUM, TIERS.PRO];

const comparisonRows = [
  {
    label: "Advanced matchmaking settings",
    values: {
      [TIERS.FREE]: "No",
      [TIERS.PREMIUM]: "Yes",
      [TIERS.PRO]: "Yes",
    },
  },
  {
    label: "Minimum active maps",
    values: {
      [TIERS.FREE]: "All maps active",
      [TIERS.PREMIUM]: "Keep at least 3",
      [TIERS.PRO]: "Can refine to 1",
    },
  },
  {
    label: "Minimum active server locations",
    values: {
      [TIERS.FREE]: "All locations active",
      [TIERS.PREMIUM]: "Keep at least 3",
      [TIERS.PRO]: "Can refine to 1",
    },
  },
  {
    label: "Queue advantage",
    values: {
      [TIERS.FREE]: "Standard",
      [TIERS.PREMIUM]: "Standard",
      [TIERS.PRO]: "Priority queue placement",
    },
  },
  {
    label: "Premium tier servers",
    values: {
      [TIERS.FREE]: "No",
      [TIERS.PREMIUM]: "Yes",
      [TIERS.PRO]: "Yes",
    },
  },
  {
    label: "Competitive extras",
    values: {
      [TIERS.FREE]: "Basic access",
      [TIERS.PREMIUM]: "Expanded queue control",
      [TIERS.PRO]: "Captain priority and ELO recalibration requests",
    },
  },
];

function PricingComparisonCell({ value, highlight }) {
  const normalized = typeof value === "string" ? value.toLowerCase() : "";
  const isBooleanLike = normalized === "yes" || normalized === "no";
  const isNegative = normalized === "no";

  if (isBooleanLike) {
    return (
      <Typography
        component="span"
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "0.75rem",
          fontWeight: 700,
          color: isNegative ? "rgba(217, 79, 61, 0.75)" : "#E8A33D",
        }}
      >
        {isNegative ? "[ NO ]" : "[ YES ]"}
      </Typography>
    );
  }

  return (
    <Typography
      component="span"
      sx={{
        fontSize: "0.95rem",
        lineHeight: 1.55,
        color: highlight ? "#ffffff" : "rgba(255,255,255,0.74)",
        fontWeight: highlight ? 700 : 400,
      }}
    >
      {value}
    </Typography>
  );
}

PricingComparisonCell.propTypes = {
  value: PropTypes.string.isRequired,
  highlight: PropTypes.bool,
};

export default function PricingPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <PublicPageLayout
      eyebrow="Pricing"
      title="Join free. Upgrade only when you want more control."
      description="Ouro is seeding its founding player base first. Free access gets you into the community, while Premium and Pro unlock deeper control over maps, server locations, queue behavior, and competitive extras."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Choose the level of control you need.</Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {planOrder.map((tier) => {
            const display = TIER_DISPLAY[tier];
            const monthlyPrice =
              tier === TIERS.FREE
                ? 0
                : TIER_PRICES[tier][BILLING_CYCLES.MONTHLY];
            const yearlyMonthlyPrice =
              tier === TIERS.FREE
                ? 0
                : TIER_PRICES[tier][BILLING_CYCLES.YEARLY];

            return (
              <Box
                key={tier}
                sx={{
                  ...sx.card,
                  borderColor:
                    tier === TIERS.PRO
                      ? "rgba(255,215,0,0.28)"
                      : tier === TIERS.PREMIUM
                        ? "rgba(232, 163, 61, 0.28)"
                        : "rgba(255,255,255,0.08)",
                  borderTop:
                    tier === TIERS.PRO
                      ? "2px solid #E8A33D"
                      : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: display.color,
                    mb: 1,
                  }}
                >
                  {display.name}
                </Typography>
                <Typography sx={sx.cardTitle}>
                  {tier === TIERS.FREE
                    ? "EUR 0"
                    : `EUR ${yearlyMonthlyPrice.toFixed(2)} / mo`}
                </Typography>
                {tier !== TIERS.FREE ? (
                  <Typography sx={{ ...sx.cardBody, mb: 2 }}>
                    Or EUR {monthlyPrice.toFixed(2)} / month on monthly billing.
                  </Typography>
                ) : (
                  <Typography sx={{ ...sx.cardBody, mb: 2 }}>
                    Join the founding community before deciding whether you need
                    deeper queue control.
                  </Typography>
                )}
                <Box component="ul" sx={sx.bulletList}>
                  {PLAN_FEATURES[tier].map((feature) => (
                    <li key={feature}>
                      <Typography component="span" sx={sx.listItemText}>
                        {feature}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Quick comparison</Typography>
        </Box>

        <Box sx={sx.tableWrap}>
          <Box component="table" sx={sx.comparisonTable}>
            <thead>
              <tr>
                <th>
                  <Typography component="span" sx={sx.tableHeadText}>
                    Feature
                  </Typography>
                </th>
                <th>
                  <Typography component="span" sx={sx.tableHeadText}>
                    Free
                  </Typography>
                </th>
                <th>
                  <Typography component="span" sx={sx.tableHeadText}>
                    Premium
                  </Typography>
                </th>
                <th>
                  <Typography component="span" sx={sx.tableHeadText}>
                    Pro
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label}>
                  <td>
                    <Typography component="span" sx={sx.tableCellText}>
                      {row.label}
                    </Typography>
                  </td>
                  <td>
                    <PricingComparisonCell value={row.values[TIERS.FREE]} />
                  </td>
                  <td>
                    <PricingComparisonCell value={row.values[TIERS.PREMIUM]} highlight />
                  </td>
                  <td>
                    <PricingComparisonCell value={row.values[TIERS.PRO]} highlight />
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Box>

      <Box sx={{ textAlign: "center", mt: 3, mb: -1 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
          Members earn 1 free month for every friend they refer — useful while each region builds density.
        </Typography>
      </Box>

      <Box sx={sx.callout}>
        <Typography sx={sx.sectionTitle}>Start by joining the founding community</Typography>
        <Typography sx={sx.sectionCopy}>
          Create your account, complete verification, and help your region reach
          healthy queue density. Pick a paid membership later if you want more
          deliberate queue control.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Button component={Link} to="/" variant="contained" sx={sx.primaryButton}>
            Join free
          </Button>
          <Button component={Link} to="/how-it-works" variant="outlined" sx={sx.secondaryButton}>
            See how Ouro works
          </Button>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
